<script setup>
import { ref, watch } from 'vue'
import { router, Link } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Star, Eye, Clock, Heart, Bookmark, Play, Flame, Search, X, Filter, ChevronDown, LockOpen } from 'lucide-vue-next';
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core";
import { useFilterTransition } from '@/composables/useFilterTransition'
const breakpoints = useBreakpoints(breakpointsTailwind)


const props = defineProps({
  contents: Object,
  featuredContent: Object,
  subjects: Array,
  levels: Array,
  examboards: Array,
  filters: Object,
})

// Filter states
/**
 * Enter takes you to the results.
 *
 * The grid begins below the search box, so on a short window typing and
 * pressing Enter moved nothing the reader could see.
 */
const resultsRef = ref(null)
const showResults = () => resultsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })

const searchQuery = ref(props.filters?.search || '')
const selectedSubjects = ref(props.filters?.subjects || [])
const selectedLevels = ref(props.filters?.levels || [])
const selectedExamboards = ref(props.filters?.examboards || [])
// Toggle functions for filter selections
const toggleSubject = (subjectSlug) => {
  const index = selectedSubjects.value.indexOf(subjectSlug)
  if (index > -1) {
    selectedSubjects.value.splice(index, 1)
  } else {
    selectedSubjects.value.push(subjectSlug)
  }
  applyFilters()
}

const toggleLevel = (levelSlug) => {
  const index = selectedLevels.value.indexOf(levelSlug)
  if (index > -1) {
    selectedLevels.value.splice(index, 1)
  } else {
    selectedLevels.value.push(levelSlug)
  }
  applyFilters()
}

const toggleExamboard = (examboardSlug) => {
  const index = selectedExamboards.value.indexOf(examboardSlug)
  if (index > -1) {
    selectedExamboards.value.splice(index, 1)
  } else {
    selectedExamboards.value.push(examboardSlug)
  }
  applyFilters()
}

const removeSubject = (subjectSlug) => {
  const index = selectedSubjects.value.indexOf(subjectSlug)
  if (index > -1) {
    selectedSubjects.value.splice(index, 1)
    applyFilters()
  }
}

const removeLevel = (levelSlug) => {
  const index = selectedLevels.value.indexOf(levelSlug)
  if (index > -1) {
    selectedLevels.value.splice(index, 1)
    applyFilters()
  }
}

const removeExamboard = (examboardSlug) => {
  const index = selectedExamboards.value.indexOf(examboardSlug)
  if (index > -1) {
    selectedExamboards.value.splice(index, 1)
    applyFilters()
  }
}

// Accordion states
const isSubjectOpen = ref(breakpoints.greaterOrEqual('md'))
const isLevelOpen = ref(breakpoints.greaterOrEqual('md'))
const isExamboardOpen = ref(breakpoints.greaterOrEqual('md'))

// Format duration from seconds to MM:SS
const formatDuration = (seconds) => {
  if (!seconds) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Format large numbers
const formatViews = (count) => {
  if (!count) return '0'
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toString()
}

// Format time ago
const formatTimeAgo = (date) => {
  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now - then) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`
  return `${Math.floor(seconds / 2592000)} months ago`
}

// Apply filters
/** The same crossfade the tutor listings use, so filtering behaves alike everywhere. */
const { pending, visit } = useFilterTransition('/library')

const applyFilters = () => {
  visit({
    search: searchQuery.value,
    subjects: selectedSubjects.value.length > 0 ? selectedSubjects.value : undefined,
    levels: selectedLevels.value.length > 0 ? selectedLevels.value : undefined,
    examboards: selectedExamboards.value.length > 0 ? selectedExamboards.value : undefined,
  })
}

// Reset all filters
const resetFilters = () => {
  searchQuery.value = ''
  selectedSubjects.value = []
  selectedLevels.value = []
  selectedExamboards.value = []
  applyFilters()
}

// Watch search query and apply filters with debounce
let searchTimeout
watch(searchQuery, () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 500)
})

// Get subject color
const getSubjectColor = (subject) => {
  if (!subject) return '#6B7280'
  return subject.color || '#6B7280'
}
</script>

<template>
  <AppLayout>
    <div class="min-h-screen bg-gray-50 py-8">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">Content Library</h1>
          <p class="text-gray-600">Explore educational videos from expert tutors across various subjects</p>
        </div>

        <!-- Search Bar -->
        <section class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div class="relative">
            <div class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search class="w-5 h-5" />
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by title, topic, or tutor name..."
              @keyup.enter="showResults"
              class="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-gray-900"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
        </section>

        <!-- Main Content with Sidebar -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <!-- Filters Sidebar -->
          <aside class="lg:col-span-1">
            <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 pb-2">

              <div class="flex items-center justify-between mb-6">
                <h2 class="text-lg font-bold text-gray-900 flex items-center">
                  <Filter class="w-5 h-5 mr-2" />
                  Filters
                </h2>
                <button
                  v-if="selectedSubjects.length > 0 || selectedLevels.length > 0 || selectedExamboards.length > 0"
                  @click="resetFilters"
                  class="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Reset
                </button>
              </div>

              <!-- Subject Filter Accordion -->
              <div class="mb-2">
                <button
                  @click="isSubjectOpen = !isSubjectOpen"
                  class="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Subject</h3>
                  <ChevronDown
                    class="w-5 h-5 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isSubjectOpen }"
                  />
                </button>
                <!-- Selected subjects tags -->
                <div v-if="selectedSubjects.length > 0" class="px-3 pt-2 pb-1 flex flex-wrap gap-2">
                  <span
                    v-for="subjectSlug in selectedSubjects"
                    :key="`selected-${subjectSlug}`"
                    class="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ subjects.find(s => s.slug === subjectSlug)?.name }}
                    <button @click="removeSubject(subjectSlug)" class="ml-1.5 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isSubjectOpen" class="pt-2 space-y-2 max-h-64 overflow-y-auto scrollbar-hover">
                    <label
                      v-for="subject in subjects"
                      :key="subject.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="subject.slug"
                        :checked="selectedSubjects.includes(subject.slug)"
                        @change="toggleSubject(subject.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700 flex-1">{{ subject.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Level Filter Accordion -->
              <div class="mb-2 border-t border-gray-200 pt-4">
                <button
                  @click="isLevelOpen = !isLevelOpen"
                  class="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Level</h3>
                  <ChevronDown
                    class="w-5 h-5 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isLevelOpen }"
                  />
                </button>
                <!-- Selected levels tags -->
                <div v-if="selectedLevels.length > 0" class="px-3 pt-2 pb-1 flex flex-wrap gap-2">
                  <span
                    v-for="levelSlug in selectedLevels"
                    :key="`selected-${levelSlug}`"
                    class="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ levels.find(l => l.slug === levelSlug)?.name }}
                    <button @click="removeLevel(levelSlug)" class="ml-1.5 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isLevelOpen" class="pt-2 space-y-2">
                    <label
                      v-for="level in levels"
                      :key="level.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="level.slug"
                        :checked="selectedLevels.includes(level.slug)"
                        @change="toggleLevel(level.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700">{{ level.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Exam Board Filter Accordion -->
              <div class="mb-2 border-t border-gray-200 pt-4">
                <button
                  @click="isExamboardOpen = !isExamboardOpen"
                  class="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <h3 class="font-bold text-gray-900 text-sm">Exam Board</h3>
                  <ChevronDown
                    class="w-5 h-5 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isExamboardOpen }"
                  />
                </button>
                <!-- Selected examboards tags -->
                <div v-if="selectedExamboards.length > 0" class="px-3 pt-2 pb-1 flex flex-wrap gap-2">
                  <span
                    v-for="examboardSlug in selectedExamboards"
                    :key="`selected-${examboardSlug}`"
                    class="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium"
                  >
                    {{ examboards.find(e => e.slug === examboardSlug)?.name }}
                    <button @click="removeExamboard(examboardSlug)" class="ml-1.5 hover:text-gray-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isExamboardOpen" class="pt-2 space-y-2">
                    <label
                      v-for="examboard in examboards"
                      :key="examboard.slug"
                      class="flex items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="examboard.slug"
                        :checked="selectedExamboards.includes(examboard.slug)"
                        @change="toggleExamboard(examboard.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="ml-3 text-sm text-gray-700">{{ examboard.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

            </div>
          </aside>

          <!-- Main Content Area -->
          <div class="lg:col-span-3">

        <!-- Featured Content -->
        <section v-if="featuredContent && !searchQuery" class="mb-8">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-900 flex items-center">
              <Star class="text-yellow-500 mr-2 w-5 h-5 fill-yellow-500" />
              Featured Today
            </h2>
          </div>

          <Link :href="`/library/${featuredContent.slug}`" class="block bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div class="grid md:grid-cols-3 gap-6">
              <!-- Video Thumbnail -->
              <div class="relative md:col-span-1">
                <div class="h-full min-h-[250px] bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                  <Play class="text-white w-16 h-16 opacity-80" />
                </div>
                <div class="absolute top-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                  {{ formatDuration(featuredContent.duration) }}
                </div>
                <div class="absolute bottom-4 left-4 bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold flex items-center">
                  <Flame class="mr-1.5 w-4 h-4" />
                  TRENDING
                </div>
              </div>

              <!-- Content Details -->
              <div class="md:col-span-2 p-6">
                <div class="flex items-start justify-between mb-4">
                  <div class="flex items-center space-x-3">
                    <img
                      :src="featuredContent.teacher?.avatar || null"
                      class="w-12 h-12 rounded-full"
                      :alt="featuredContent.teacher?.name"
                    />
                    <div>
                      <h3 class="font-bold text-gray-900 text-lg">{{ featuredContent.title }}</h3>
                      <p class="text-sm text-gray-500">by {{ featuredContent.teacher?.name }}</p>
                    </div>
                  </div>
                  <button class="text-gray-400 hover:text-red-500 transition-colors">
                    <Heart class="w-6 h-6" />
                  </button>
                </div>

                <p class="text-gray-600 mb-4 leading-relaxed line-clamp-2">
                  {{ featuredContent.description }}
                </p>

                <div class="flex items-center justify-between flex-wrap gap-4">
                  <div class="flex items-center space-x-6 text-sm">
                    <span class="flex items-center text-gray-600">
                      <Eye class="mr-2 w-4 h-4" />
                      <span class="font-medium">{{ formatViews(featuredContent.view_count) }} views</span>
                    </span>
                    <span class="flex items-center text-gray-600">
                      <Star class="text-yellow-400 mr-2 w-4 h-4 fill-yellow-400" />
                      <span class="font-medium">{{ featuredContent.ratings_avg_score ? Number(featuredContent.ratings_avg_score).toFixed(1) : 'N/A' }}</span>
                      <span class="text-sm text-gray-400 ml-1">({{ featuredContent.ratings_count || 0 }} ratings)</span>
                    </span>
                    <span class="flex items-center text-gray-600">
                      <Clock class="mr-2 w-4 h-4" />
                      <span class="font-medium">{{ formatTimeAgo(featuredContent.created_at) }}</span>
                    </span>
                  </div>

                  <div class="flex items-center space-x-2">
                    <span
                      v-if="featuredContent.is_free"
                      class="flex items-center px-2.5 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg font-medium text-sm"
                      title="Free to watch"
                    >
                      <LockOpen class="w-4 h-4 mr-1" />
                      FREE
                    </span>
                    <span
                      v-if="featuredContent.subject"
                      class="px-3 py-1.5 rounded-full font-medium text-sm"
                      :style="{
                        backgroundColor: `${getSubjectColor(featuredContent.subject)}20`,
                        color: getSubjectColor(featuredContent.subject)
                      }"
                    >
                      {{ featuredContent.subject.name }}
                    </span>
                    <button class="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center">
                      <Play class="mr-2 w-4 h-4" />
                      Watch Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </section>

        <!-- All Videos Grid -->
        <section>
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-900">All Videos</h2>
            <span class="text-sm text-gray-500">
              Showing {{ contents.data.length }} of {{ contents.total }} videos
            </span>
          </div>

          <!-- Video Grid -->
          <div v-if="contents.data.length > 0" ref="resultsRef" class="scroll-mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Link
              v-for="content in contents.data"
              :key="content.id"
              :href="`/library/${content.slug}`"
              class="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all hover:-translate-y-1"
            >
              <!-- Thumbnail -->
              <div class="relative">
                <div
                  class="h-48 bg-gradient-to-br flex items-center justify-center"
                  :class="[
                    content.id % 5 === 0 ? 'from-teal-400 to-teal-600' :
                    content.id % 5 === 1 ? 'from-teal-400 to-teal-600' :
                    content.id % 5 === 2 ? 'from-teal-400 to-teal-600' :
                    content.id % 5 === 3 ? 'from-teal-400 to-teal-600' :
                    'from-teal-400 to-teal-600'
                  ]"
                >
                  <Play class="text-white w-10 h-10 opacity-80" />
                </div>
                <div class="absolute top-3 right-3 bg-black/70 text-white px-2.5 py-1 rounded-lg text-xs font-medium">
                  {{ formatDuration(content.duration) }}
                </div>
              </div>

              <!-- Content Details -->
              <div class="p-4">
                <div class="flex items-start justify-between mb-2">
                  <div class="flex items-center space-x-2 flex-1 min-w-0">
                    <img
                      :src="content.teacher?.avatar || null"
                      class="w-9 h-9 rounded-full flex-shrink-0"
                      :alt="content.teacher?.name"
                    />
                    <div class="flex-1 min-w-0">
                      <h3 class="font-bold text-gray-900 text-sm truncate">{{ content.title }}</h3>
                      <p class="text-xs text-gray-500 truncate">{{ content.teacher?.name }}</p>
                    </div>
                  </div>
                  <button @click.prevent class="text-gray-400 hover:text-blue-600 transition-colors ml-2 flex-shrink-0">
                    <Bookmark class="w-4 h-4" />
                  </button>
                </div>

                <p class="text-xs text-gray-600 mb-3 line-clamp-2">
                  {{ content.description }}
                </p>

                <div class="flex items-center justify-between text-xs">
                  <div class="flex items-center space-x-3">
                    <span class="flex items-center text-gray-600">
                      <Eye class="mr-1 w-3 h-3" />
                      {{ formatViews(content.view_count) }}
                    </span>
                    <span class="flex items-center text-gray-600">
                      <Star class="text-yellow-400 mr-1 w-3 h-3 fill-yellow-400" />
                      {{ content.ratings_avg_score ? Number(content.ratings_avg_score).toFixed(1) : 'N/A' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <span
                      v-if="content.is_free"
                      class="flex items-center px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium"
                      title="Free to watch"
                    >
                      <LockOpen class="w-3 h-3" />
                    </span>
                    <span
                      v-if="content.subject"
                      class="px-2 py-1 rounded-full font-medium"
                      :style="{
                        backgroundColor: `${getSubjectColor(content.subject)}20`,
                        color: getSubjectColor(content.subject)
                      }"
                    >
                      {{ content.subject.name }}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <Search class="w-16 h-16 mx-auto" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">No videos found</h3>
            <p class="text-gray-600 mb-4">Try adjusting your filters or search query</p>
            <button
              @click="resetFilters"
              class="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>

          <!-- Pagination -->
          <div v-if="contents.data.length > 0 && (contents.prev_page_url || contents.next_page_url)" class="flex items-center justify-center space-x-2">
            <Link
              v-if="contents.prev_page_url"
              :href="contents.prev_page_url"
              preserve-state
              preserve-scroll
              class="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Previous
            </Link>
            <span class="px-4 py-2 text-gray-600">
              Page {{ contents.current_page }} of {{ contents.last_page }}
            </span>
            <Link
              v-if="contents.next_page_url"
              :href="contents.next_page_url"
              preserve-state
              preserve-scroll
              class="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Next
            </Link>
          </div>
        </section>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
